import React from 'react';
import {
  FEATURE_ALIGNMENT_CONTROL,
  FEATURE_AUDIO_FADE_CONTROL,
  FEATURE_BORDER_RADIUS_CONTROL,
  FEATURE_DIMENSIONS_CONTROL,
  FEATURE_OPACITY_CONTROL,
  FEATURE_PLAYBACKRATE_CONTROL,
  FEATURE_POSITION_CONTROL,
  FEATURE_ROTATION_CONTROL,
  FEATURE_VOLUME_CONTROL,
} from '../flags';
import { VideoItem } from '../items/video/video-item-type';
import { useAssetFromItem } from '../utils/use-context';
import { AccordionHeader } from '@crabs-ai/ds-react';
import { AlignmentControls } from './controls/alignment-controls';
import { AudioFadeControls } from './controls/audio-fade-controls';
import { BorderRadiusControl } from './controls/border-radius-controls';
import { DimensionsControls } from './controls/dimensions-controls';
import { PlaybackRateControls } from './controls/playback-rate-controls';
import { PositionControl } from './controls/position-control';
import { RotationControl } from './controls/rotation-controls';
import { VolumeControls } from './controls/volume-controls';
import { useTranslations } from 'next-intl';
import { ClipOpacityControls } from '@/features/remotion-editor/editor/inspector/controls/clip-opacity-controls';
import LayoutControl from '@/features/remotion-editor/editor/inspector/controls/layout-control';
import { AnimationControls } from './controls/animation-controls';
import { EMPTY_ANIMATIONS } from '../items/animation/animation-item-type';
import { useInspectorSections } from './use-inspector-sections';
import { CollapsibleContent } from './collapsible-content';

const VIDEO_DEFAULTS = {
  layout: true,
  positionShape: true,
  playbackSpeed: true,
  volume: true,
};

const VideoInspectorUnmemoized: React.FC<{
  item: VideoItem;
  showVideoControls?: boolean;
  showVolumeControls?: boolean;
  showLabel?: boolean;
  isMulti?: boolean;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}> = ({
  item,
  showVideoControls = true,
  showVolumeControls = true,
  collapseAllTrigger = 0,
  expandAllTrigger = 0,
}) => {
  const t = useTranslations();
  const asset = useAssetFromItem(item);
  const { openSections, toggleSection } = useInspectorSections({
    itemId: item.id,
    defaults: VIDEO_DEFAULTS,
    collapseAllTrigger,
    expandAllTrigger,
  });

  if (asset.type !== 'video') {
    throw new Error('Video inspector not supported for video assets');
  }

  return (
    <div>
      {/*{FEATURE_SOURCE_CONTROL && <SourceControls item={item} />}*/}
      <div className="flex flex-col gap-2xs">
        {/* Animation 섹션 */}
        <div className="border-b border-sys-style-neutral-light">
          <AnimationControls
            itemId={item.id}
            animations={item.animations ?? EMPTY_ANIMATIONS}
            durationInFrames={item.durationInFrames}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.layout-setting')}
            isShowTooltipIcon={true}
            tooltipText={t('Editor.inspector.layout-setting-tooltip')}
            isOpened={openSections.layout}
            onClick={() => toggleSection('layout')}
          />
          <CollapsibleContent isOpen={openSections.layout}>
            <div className={'px-md py-4xs'}>
              <LayoutControl item={item} />
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.video.position-shape')}
            isOpened={openSections.positionShape}
            onClick={() => toggleSection('positionShape')}
          />
          <CollapsibleContent isOpen={openSections.positionShape}>
            <div className="gap-4xs px-md py-4xs">
              {FEATURE_ALIGNMENT_CONTROL && (
                <AlignmentControls itemId={item.id} />
              )}
              {FEATURE_POSITION_CONTROL && <PositionControl itemId={item.id} />}
              {FEATURE_DIMENSIONS_CONTROL && (
                <DimensionsControls itemId={item.id} />
              )}
              {FEATURE_ROTATION_CONTROL && (
                <RotationControl
                  rotation={item.rotation}
                  itemId={item.id}
                />
              )}
              <div className="flex w-full items-center gap-4xs">
                {FEATURE_OPACITY_CONTROL && (
                  <ClipOpacityControls
                    opacity={item.opacity}
                    itemId={item.id}
                  />
                )}
                {FEATURE_BORDER_RADIUS_CONTROL && (
                  <BorderRadiusControl
                    borderRadius={item.borderRadius}
                    itemId={item.id}
                  />
                )}
              </div>
            </div>
          </CollapsibleContent>
        </div>
        {showVideoControls && FEATURE_PLAYBACKRATE_CONTROL && (
          <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.playback-speed')}
              isOpened={openSections.playbackSpeed}
              onClick={() => toggleSection('playbackSpeed')}
            />
            <CollapsibleContent isOpen={openSections.playbackSpeed}>
              <div className="flex flex-col gap-4xs px-md py-4xs">
                <PlaybackRateControls
                  playbackRate={item.playbackRate}
                  itemId={item.id}
                />
              </div>
            </CollapsibleContent>
          </div>
        )}
        {showVolumeControls && asset.hasAudioTrack ? (
          <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.volume-control')}
              isOpened={openSections.volume}
              onClick={() => toggleSection('volume')}
            />
            <CollapsibleContent isOpen={openSections.volume}>
              <div className="px-md py-4xs">
                {FEATURE_VOLUME_CONTROL && (
                  <VolumeControls
                    decibelAdjustment={item.decibelAdjustment}
                    itemId={item.id}
                  />
                )}
                {FEATURE_AUDIO_FADE_CONTROL && (
                  <AudioFadeControls
                    fadeInDuration={item.audioFadeInDurationInSeconds}
                    fadeOutDuration={item.audioFadeOutDurationInSeconds}
                    itemId={item.id}
                    durationInFrames={item.durationInFrames}
                  />
                )}
              </div>
            </CollapsibleContent>
          </div>
        ) : null}
      </div>
      <div className={'layout-padding-bottom'} />
    </div>
  );
};

export const VideoInspector = React.memo(VideoInspectorUnmemoized);
